import { useCallback } from 'react';
import { useAppContext } from '../contexts/AppContext';

export function useSelection() {
  const {
    selectedIndices,
    setSelectedIndices,
    primaryIndex,
    setPrimaryIndex,
    files
  } = useAppContext();

  const selectSingle = useCallback((index) => {
    setSelectedIndices(new Set([index]));
    setPrimaryIndex(index);
  }, [setSelectedIndices, setPrimaryIndex]);

  const toggleSelect = useCallback((index) => {
    const newSelected = new Set(selectedIndices);
    if (newSelected.has(index)) {
      newSelected.delete(index);
      if (primaryIndex === index && newSelected.size > 0) {
        setPrimaryIndex(Math.min(...newSelected));
      }
    } else {
      newSelected.add(index);
      setPrimaryIndex(index);
    }
    setSelectedIndices(newSelected);
  }, [selectedIndices, primaryIndex, setSelectedIndices, setPrimaryIndex]);

  const rangeSelect = useCallback((fromIndex, toIndex) => {
    const newSelected = new Set();
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    for (let i = start; i <= end; i++) {
      newSelected.add(i);
    }
    setSelectedIndices(newSelected);
    setPrimaryIndex(toIndex);
  }, [setSelectedIndices, setPrimaryIndex]);

  const selectAll = useCallback(() => {
    const allIndices = new Set(files.map((_, i) => i));
    setSelectedIndices(allIndices);
  }, [files, setSelectedIndices]);

  const clearSelection = useCallback(() => {
    setSelectedIndices(new Set());
    setPrimaryIndex(-1);
  }, [setSelectedIndices, setPrimaryIndex]);

  const isSelected = useCallback((index) => {
    return selectedIndices.has(index);
  }, [selectedIndices]);

  return {
    selectedIndices,
    primaryIndex,
    selectSingle,
    toggleSelect,
    rangeSelect,
    selectAll,
    clearSelection,
    isSelected
  };
}
